import React, { useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Button, SearchableDropdown } from '@/components';
import { SideBarModal } from '@/components/Modal';
import { RepossessionSettlementType } from '@/types/common';
import {
  extractApiErrorMessage,
  getBucketUrlFile,
  uploadTempFile,
  fileToBase64,
} from '@/utils';
import {
  showErrorToast,
  showSuccessToast,
} from '@/components/toast/globalToast';

interface ProcessSettlementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (payload: {
    settlementAmount: number;
    settlementType: RepossessionSettlementType;
    settlementNotes: string;
    settlementDocumentURL: string;
    financeApproval: boolean;
  }) => void;
  isSubmitting?: boolean;
}

const ProcessSettlementModal: React.FC<ProcessSettlementModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [settlementAmountInput, setSettlementAmountInput] = useState('');
  const [settlementType, setSettlementType] = useState<
    RepossessionSettlementType | ''
  >('');
  const [settlementNotes, setSettlementNotes] = useState('');
  const [financeApproval, setFinanceApproval] = useState(false);
  const [tempFile, setTempFile] = useState<{
    name: string;
    base64: string;
    size: number;
    mimeType: string;
  } | null>(null);
  const [tempUploadUrl, setTempUploadUrl] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);

  const formatter = useMemo(
    () =>
      new Intl.NumberFormat('id-ID', {
        style: 'currency',
        currency: 'IDR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }),
    []
  );

  const settlementTypeOptions = [
    {
      value: RepossessionSettlementType.INSURANCE_CLAIM,
      label: 'Insurance Claim',
    },
    {
      value: RepossessionSettlementType.OEM_LIABILITY,
      label: 'OEM Liability',
    },
    {
      value: RepossessionSettlementType.DRIVER_LIABILITY,
      label: 'Driver Liability',
    },
    {
      value: RepossessionSettlementType.COMPANY_LOSS,
      label: 'Company Loss',
    },
    {
      value: RepossessionSettlementType.OTHER,
      label: 'Other',
    },
  ];

  const parsedAmount = settlementAmountInput
    ? Number(settlementAmountInput)
    : NaN;
  const settlementAmount = settlementAmountInput
    ? Number.isNaN(parsedAmount)
      ? null
      : parsedAmount
    : null;

  const resetState = () => {
    setSettlementAmountInput('');
    setSettlementType('');
    setSettlementNotes('');
    setFinanceApproval(false);
    setTempFile(null);
    setTempUploadUrl('');
    setIsUploading(false);
    setIsDragActive(false);
  };

  useEffect(() => {
    if (!isOpen) {
      resetState();
    }
  }, [isOpen]);

  const handleClose = () => {
    resetState();
    onClose();
  };

  const formatFileSize = (size: number) => {
    if (!size) return '0 KB';
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(0)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleFileSelection = async (file: File | null) => {
    if (!file) return;

    if (file.type !== 'application/pdf') {
      showErrorToast('Error', 'Only PDF files are allowed.');
      return;
    }

    try {
      setIsUploading(true);
      const base64Data = await fileToBase64(file);

      const uuid = uuidv4();
      const response = await getBucketUrlFile({
        uuid,
        fileType: 'repossessions',
        fileFormat: 'pdf',
      });

      if (!response?.data) {
        throw new Error('Failed to get upload URL');
      }

      const uploadUrl = response.data;
      const uploadRes = await uploadTempFile(
        uploadUrl,
        base64Data,
        file.name,
        file.type
      );

      if (uploadRes.status !== 200) {
        throw new Error('Upload failed');
      }

      setTempFile({
        name: file.name,
        base64: base64Data,
        size: file.size,
        mimeType: file.type,
      });
      setTempUploadUrl(uploadUrl);
      showSuccessToast('Success', 'Upload PDF success');
    } catch (error) {
      const errorMessage = extractApiErrorMessage(
        error,
        'Failed to upload PDF'
      );
      showErrorToast('Error', errorMessage);
      setTempFile(null);
      setTempUploadUrl('');
    } finally {
      setIsUploading(false);
    }
  };

  const handleImageUpload = () => {
    fileInputRef.current?.click();
  };

  const handleBrowseFile = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    handleFileSelection(file);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragActive(false);
    const file = event.dataTransfer.files?.[0] || null;
    handleFileSelection(file);
  };

  const handleRemoveImage = () => {
    setTempFile(null);
    setTempUploadUrl('');
  };

  const handleSubmit = async () => {
    if (settlementAmount === null) return;

    try {
      setIsUploading(true);

      onSubmit({
        settlementAmount,
        settlementType: settlementType as RepossessionSettlementType,
        settlementNotes: settlementNotes.trim(),
        settlementDocumentURL: tempUploadUrl || '',
        financeApproval,
      });
    } catch (error) {
      const errorMessage = extractApiErrorMessage(
        error,
        'Failed to upload settlement document'
      );
      showErrorToast('Error', errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  const isSettlementNotesInvalid =
    settlementNotes.trim().length > 0 && settlementNotes.trim().length < 20;

  const isFormValid =
    settlementAmount !== null &&
    settlementAmount >= 0 &&
    settlementType &&
    settlementNotes.trim().length >= 20 &&
    financeApproval &&
    !isUploading &&
    !isSubmitting;

  const formatAmountValue = () => {
    if (!settlementAmountInput) return '';
    const numericValue = Number(settlementAmountInput);
    if (Number.isNaN(numericValue)) return '';
    return formatter.format(numericValue);
  };

  return (
    <SideBarModal
      isOpen={isOpen}
      onClose={handleClose}
      position="right"
      width="sm"
      maxHeight="screen"
      header={
        <div>
          <div className="text-base font-semibold text-gray-900">
            Process Settlement
          </div>
          <div className="text-xs text-gray-500">
            Input approved settlement details to resolve the liability.
          </div>
        </div>
      }
      footer={
        <div className="flex w-full justify-end">
          <Button
            onClick={handleSubmit}
            disabled={!isFormValid}
            className="w-full"
            variant={'primary'}
          >
            {isSubmitting || isUploading
              ? 'Processing...'
              : 'Complete Settlement & Close'}
          </Button>
        </div>
      }
      className="max-w-[420px]"
    >
      <div className="bg-gray-50 px-4 py-2 text-[12px] font-medium text-gray-500 rounded-md">
        Detail
      </div>
      <div className="px-6 py-4">
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Settlement Amount<span className="text-red-500"> *</span>
            </label>
            <div className="relative">
              <input
                type="text"
                inputMode="numeric"
                value={formatAmountValue()}
                onChange={(event) => {
                  const digitsOnly = event.target.value.replace(/\D/g, '');
                  setSettlementAmountInput(digitsOnly);
                }}
                placeholder="Rp 0"
                className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-200"
              />
              <span className="absolute inset-y-0 right-3 flex items-center text-sm text-gray-400">
                IDR
              </span>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Settlement Type<span className="text-red-500"> *</span>
            </label>
            <div className="relative">
              <SearchableDropdown
                options={settlementTypeOptions}
                value={settlementType || undefined}
                onChange={(value) =>
                  setSettlementType(value as RepossessionSettlementType)
                }
                placeholder="e.g. OEM Liability"
                className="w-full"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Settlement Notes<span className="text-red-500"> *</span>
            </label>
            <div className="relative">
              <textarea
                value={settlementNotes}
                onChange={(event) => setSettlementNotes(event.target.value)}
                placeholder="Describe settlement agreement, include reference numbers."
                rows={4}
                className={`w-full rounded-md border px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-200 ${
                  isSettlementNotesInvalid
                    ? 'border-red-500 focus:ring-red-200'
                    : 'border-gray-200'
                }`}
              />
              <div className="absolute bottom-2 right-3 text-[10px] text-gray-400">
                {Math.min(settlementNotes.length, 200)}/200
              </div>
              {isSettlementNotesInvalid && (
                <div className="mt-1 text-[11px] text-red-500">
                  Minimum 20 characters
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              File (PDF Only)
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              className="hidden"
              onChange={handleFileChange}
            />
            <div className="border-2 border-dashed border-gray-300 rounded-lg w-full">
              {isUploading ? (
                <div className="p-8 flex flex-col justify-center items-center text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                  <p className="mt-2 text-sm text-gray-600">
                    Uploading document...
                  </p>
                </div>
              ) : !tempFile ? (
                <div
                  onClick={handleImageUpload}
                  onDragOver={(event) => {
                    event.preventDefault();
                    setIsDragActive(true);
                  }}
                  onDragLeave={() => setIsDragActive(false)}
                  onDrop={handleDrop}
                  className={`p-8 flex flex-col justify-center items-center text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-all ${
                    isDragActive ? 'bg-blue-50' : ''
                  }`}
                >
                  <div className="text-gray-400 text-4xl mb-4">
                    <svg
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      className="w-12 h-12 mx-auto"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 15a4 4 0 004 4h10a4 4 0 000-8 5 5 0 00-9.7-1.7A4.5 4.5 0 003 15z"
                      />
                    </svg>
                  </div>
                  <p className="font-semibold text-gray-700">
                    Add a settlement document
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    PDF format, up to 10 MB.
                  </p>
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleBrowseFile();
                    }}
                    className="mt-4 bg-white border border-gray-300 text-gray-700 text-sm font-semibold py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Browse File
                  </button>
                </div>
              ) : (
                <div className="p-4 flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="text-gray-600">
                      <svg
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        className="w-8 h-8"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 15a4 4 0 004 4h10a4 4 0 000-8 5 5 0 00-9.7-1.7A4.5 4.5 0 003 15z"
                        />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-800">
                        {tempFile.name || 'Settlement Document'}
                      </p>
                      <div className="flex items-center space-x-2 text-xs text-gray-500 mt-1">
                        <span>{formatFileSize(tempFile.size)}</span>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="text-green-500"
                        >
                          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                          <polyline points="22 4 12 14.01 9 11.01" />
                        </svg>
                        <span className="text-green-600 font-medium">
                          Completed
                        </span>
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className="text-gray-500 hover:text-red-500 transition-colors"
                  >
                    <svg
                      className="w-5 h-5 text-red-500 hover:text-red-700 cursor-pointer"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="bg-gray-50 px-4 py-2 text-[12px] font-medium text-gray-500 rounded-md">
        Confirm Transfer
      </div>
      <label className="mt-2 px-4 flex items-start gap-2 text-[14px] ">
        <input
          type="checkbox"
          checked={financeApproval}
          onChange={(event) => setFinanceApproval(event.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-200"
        />
        <span>I confirm that Finance team has approved this settlement.</span>
      </label>
    </SideBarModal>
  );
};

export default ProcessSettlementModal;
